import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * Provider-neutral geographic-coverage editor for a single provider scope.
 *
 * This is the shared UX primitive behind the standardized multi-cloud model:
 *   provider scope (AWS account / Azure subscription) → geographic scope
 *   (AWS region / Azure location).
 *
 * It is intentionally presentational and provider-agnostic in *structure* while
 * staying provider-native in *language*: the parent passes the native noun
 * (`region` / `location`) and the native item set, so the layout, spacing, and
 * control placement are identical across providers without flattening the
 * concepts into generic jargon. It owns no data or services — the parent keeps
 * its provider-native state and persistence.
 */
@Component({
  selector: 'app-geography-scope-editor',
  template: `
    <div class="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div *ngIf="title || description">
        <p *ngIf="title" class="text-xs font-semibold text-slate-700">{{ title }}</p>
        <p *ngIf="description" class="mt-0.5 text-[11px] text-slate-500">{{ description }}</p>
      </div>

      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label
          *ngFor="let item of items"
          class="flex items-center gap-2.5 rounded-lg border bg-white px-3 py-2 text-xs transition-colors"
          [class.border-violet-300]="isEnabled(item.code)"
          [class.bg-violet-50]="isEnabled(item.code)"
          [class.border-slate-200]="!isEnabled(item.code)"
        >
          <input
            type="checkbox"
            class="h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
            [checked]="isEnabled(item.code)"
            [disabled]="disabled || item.code === defaultCode"
            (change)="toggle.emit(item.code)"
          />
          <span class="min-w-0 flex-1">
            <span class="font-mono font-medium text-slate-800">{{ item.code }}</span>
            <span class="ml-1 text-slate-400">{{ item.label }}</span>
          </span>
          <span *ngIf="item.code === defaultCode" class="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">Default</span>
        </label>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <label [attr.for]="selectId" class="text-xs font-medium text-slate-700">Default {{ geoNoun }}</label>
        <select
          [attr.id]="selectId"
          [ngModel]="defaultCode"
          (ngModelChange)="defaultCodeChange.emit($event)"
          [disabled]="disabled"
          class="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:opacity-60"
        >
          <option *ngFor="let code of enabled" [value]="code">{{ code }}</option>
        </select>
        <div class="ml-auto flex items-center gap-2">
          <button
            *ngIf="showCancel"
            type="button"
            (click)="cancel.emit()"
            [disabled]="saving"
            class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
          >Cancel</button>
          <button
            type="button"
            (click)="save.emit()"
            [disabled]="saving || disabled"
            class="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
          >{{ saving ? 'Saving…' : ('Save ' + geoNounPlural) }}</button>
        </div>
      </div>

      <p *ngIf="error" class="text-xs text-red-600">{{ error }}</p>
    </div>
  `,
})
export class GeographyScopeEditorComponent {
  /** The full set of selectable geographies (AWS regions / Azure locations). */
  @Input() items: { code: string; label: string }[] = [];
  /** Currently-enabled geography codes for this scope. */
  @Input() enabled: string[] = [];
  /** The default geography (must be one of `enabled`; cannot be toggled off). */
  @Input() defaultCode = '';
  /** Native singular noun for copy, e.g. 'region' (AWS) or 'location' (Azure). */
  @Input() geoNoun = 'region';
  /** Native plural noun for the save button, e.g. 'regions' / 'locations'. */
  @Input() geoNounPlural = 'regions';
  /** Optional heading + description shown above the grid. */
  @Input() title = '';
  @Input() description = '';
  /** Distinct id for the default-selector label association. */
  @Input() selectId = 'geo-default';
  @Input() saving = false;
  @Input() error: string | null = null;
  @Input() disabled = false;
  @Input() showCancel = true;

  @Output() toggle = new EventEmitter<string>();
  @Output() defaultCodeChange = new EventEmitter<string>();
  @Output() save = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  isEnabled(code: string): boolean {
    return this.enabled.includes(code);
  }
}
